import { useEffect, useMemo, useState } from "react";
import { DownloadSimple, FunnelSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";

function displayDate(timestamp) {
  return new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp));
}

export default function AdminTrainerFeedbackReport({ centers }) {
  const [trainers, setTrainers] = useState([]);
  const [filters, setFilters] = useState({
    centerId: "",
    endDate: "",
    startDate: "",
    trainerId: "",
  });
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    api
      .get("/lms/trainers")
      .then((response) => setTrainers(response.data))
      .catch((requestError) => setError(formatApiError(requestError)));
  }, []);

  useEffect(() => {
    const params = {
      center_id: filters.centerId || undefined,
      end_date: filters.endDate || undefined,
      start_date: filters.startDate || undefined,
      trainer_id: filters.trainerId || undefined,
    };

    api
      .get("/trainer-feedback/admin", { params })
      .then((response) => {
        setItems(response.data.items);
        setError("");
      })
      .catch((requestError) => setError(formatApiError(requestError)));
  }, [filters]);

  const visibleTrainers = useMemo(
    () =>
      trainers.filter(
        (trainer) => !filters.centerId || trainer.center_id === filters.centerId,
      ),
    [filters.centerId, trainers],
  );

  const updateCenter = (centerId) => {
    setFilters((current) => ({
      ...current,
      centerId,
      trainerId: "",
    }));
  };

  const exportFeedback = async () => {
    setIsExporting(true);
    try {
      const response = await api.get("/trainer-feedback/admin/export", {
        params: {
          center_id: filters.centerId || undefined,
          end_date: filters.endDate || undefined,
          start_date: filters.startDate || undefined,
          trainer_id: filters.trainerId || undefined,
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "trainer_feedback.csv";
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Trainer feedback CSV downloaded.");
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <section className="attendance-report" data-testid="admin-trainer-feedback-report">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Trainer feedback</p>
          <h2>Center-wide feedback</h2>
        </div>
        <button
          className="outline-button"
          disabled={isExporting}
          onClick={exportFeedback}
          data-testid="export-admin-trainer-feedback-button"
        >
          <DownloadSimple aria-hidden="true" size={18} />
          {isExporting ? "Preparing CSV" : "Export CSV"}
        </button>
      </div>
      <div className="attendance-report-controls feedback-filter-controls">
        <label>
          Center
          <select
            value={filters.centerId}
            onChange={(event) => updateCenter(event.target.value)}
            data-testid="admin-feedback-center-filter"
          >
            <option value="">All centers</option>
            {centers.map((center) => (
              <option key={center.id} value={center.id}>
                {center.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Trainer
          <select
            value={filters.trainerId}
            onChange={(event) => setFilters((current) => ({
              ...current,
              trainerId: event.target.value,
            }))}
            data-testid="admin-feedback-trainer-filter"
          >
            <option value="">All trainers</option>
            {visibleTrainers.map((trainer) => (
              <option key={trainer.id} value={trainer.id}>
                {trainer.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          From
          <input
            type="date"
            value={filters.startDate}
            onChange={(event) => setFilters((current) => ({
              ...current,
              startDate: event.target.value,
            }))}
            data-testid="admin-feedback-start-date-filter"
          />
        </label>
        <label>
          To
          <input
            type="date"
            value={filters.endDate}
            onChange={(event) => setFilters((current) => ({
              ...current,
              endDate: event.target.value,
            }))}
            data-testid="admin-feedback-end-date-filter"
          />
        </label>
        <span className="feedback-filter-status" data-testid="admin-feedback-filter-status">
          <FunnelSimple aria-hidden="true" size={16} />
          {items ? `${items.length} entries` : "Loading entries"}
        </span>
      </div>
      {error && <p className="form-error" data-testid="admin-feedback-report-error">{error}</p>}
      {items?.length === 0 && (
        <div className="attendance-empty-state" data-testid="admin-feedback-report-empty">
          No trainer feedback matches these filters.
        </div>
      )}
      {items?.length > 0 && (
        <div className="data-table-wrap">
          <table data-testid="admin-feedback-report-table">
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Center</th>
                <th>Trainer</th>
                <th>Source</th>
                <th>Rating</th>
                <th>Feedback</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} data-testid={`admin-feedback-row-${item.id}`}>
                  <td>{displayDate(item.created_at)}</td>
                  <td>{item.center_name}</td>
                  <td>{item.trainer_name}</td>
                  <td>{item.type}</td>
                  <td>{item.rating === null ? "—" : `${item.rating} / 5`}</td>
                  <td>{item.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}